import { ref, computed, watch, onMounted, type Ref } from "vue"
import useAutomaticPriceAgreementRetrieval from "@/composables/administration/automatic-price-agreement-retrieval"
import type { Project } from "@/models/project.model"
import { ModelFactory } from "@/factories/model-factory"
import type { AbstractProjectAdministrativeItemController } from "@/controllers/model-controllers/abstract/abstract-project-administrative-item-controller"
import {
  type AdministrativeItem,
  type AdministrativeItemPriceAgreement,
  isRentalItem,
  isTransactionItem,
  type ProjectRentalItem,
  type ProjectTransactionItem
} from "@/types/models/administrative-events"
import type { ModelConstructorSignature } from "@/models/types/model-constructor-signature"
import { PurchaseItem } from "@/models/purchase-item.model"
import { SaleItem } from "@/models/sale-item.model"
import { RentalIntakeItem } from "@/models/rental-intake-item.model"
import { RentalDispenseItem } from "@/models/rental-dispense-item.model"
import type { useProjectsPurchaseItemsStore } from "@/store/projects-purchase-items.store"
import type { useProjectsSaleItemsStore } from "@/store/projects-sale-items.store"
import type { useProjectsRentalIntakeItemsStore } from "@/store/projects-rental-intake-items.store"
import type { useProjectsRentalDispenseItemsStore } from "@/store/projects-rental-dispense-items.store"
import { ProjectPurchaseItem } from "@/models/project-purchase-item.model"
import { ProjectSaleItem } from "@/models/project-sale-item.model"
import { ProjectRentalIntakeItem } from "@/models/project-rental-intake-item.model"
import { ProjectRentalDispenseItem } from "@/models/project-rental-dispense-item.model"
import { KnownBusinessEntitySaleItem } from "@/models/known-business-entity-sale-item.model.ts"
import { KnownBusinessEntityRentalDispenseItem } from "@/models/known-business-entity-rental-dispense-item.model.ts"
import { useKnownBusinessEntitiesSaleItemsStore } from "@/store/known-business-entities-sale-items.store.ts"
import type { useKnownBusinessEntitiesRentalDispenseItemsStore } from "@/store/known-business-entities-rental-dispense-items.store.ts"
import type { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import { type Moment } from "moment"
import { type IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"

export function useModelEditFormPriceAgreementManager<
  Item extends AdministrativeItem,
  LevelAdministrativeItem extends AdministrativeItemPriceAgreement
>(
  project: Project, // The project for which to manage the price agreement.
  item: Item,
  store: ReturnType<
    | typeof useProjectsPurchaseItemsStore
    | typeof useProjectsSaleItemsStore
    | typeof useProjectsRentalIntakeItemsStore
    | typeof useProjectsRentalDispenseItemsStore
    | typeof useKnownBusinessEntitiesSaleItemsStore
    | typeof useKnownBusinessEntitiesRentalDispenseItemsStore
  >,
  controller: AbstractProjectAdministrativeItemController<LevelAdministrativeItem, any, any>,
  itemType:
    | ModelConstructorSignature<any, LevelAdministrativeItem>
    | typeof ProjectPurchaseItem
    | typeof ProjectSaleItem
    | typeof ProjectRentalIntakeItem
    | typeof ProjectRentalDispenseItem
    | typeof KnownBusinessEntitySaleItem
    | typeof KnownBusinessEntityRentalDispenseItem
) {
  const _priceAgreement: Ref<LevelAdministrativeItem | undefined> = ref() // The price agreement for the item.
  const wasChanged = ref(false) // Whether user made input.

  const priceAgreement = computed<LevelAdministrativeItem>({
    get: () => _priceAgreement.value! || ({} as LevelAdministrativeItem), // Satisfies type checker
    set: (value: LevelAdministrativeItem) => {
      _priceAgreement.value = value
    }
  })

  const projectComp = computed(() => project)
  const { isRetrieving, retrieve } = useAutomaticPriceAgreementRetrieval(projectComp, true)

  onMounted(() => {
    retrieve() // Retrieve price agreement immediately
  })

  watch(isRetrieving, (val) => {
    if (!val) {
      // Retrieve price agreement from store after retrieval
      getPriceAgreementFromStore()
    }
  })

  function getPriceAgreementFromStore() {
    const existingAgreement = store.getPriceAgreement(getLevel(), item)
    if (existingAgreement) {
      _priceAgreement.value = existingAgreement as LevelAdministrativeItem
    } else {
      _priceAgreement.value = ModelFactory.createModel(
        itemType as ModelConstructorSignature<any, LevelAdministrativeItem>,
        {
          [getLevelKey()]: getLevel().id,
          [getItemKey()]: item.id,
          [getUnitPriceKey()]: ""
        } as any
      ) as LevelAdministrativeItem
    }
  }

  async function savePriceAgreement(retroactiveDate?: Moment) {
    if (!_priceAgreement.value || !wasChanged.value) {
      return
    }
    const requestConfig: IRepositoryRequestConfig = {}
    if (retroactiveDate) {
      requestConfig.query = { retroactiveDate: retroactiveDate.format("YYYY-MM-DD") }
    }

    const hasUnitPrice = ((_priceAgreement.value as any)[getUnitPriceKey()] ?? "").length > 0
    const hasSurchargeModifier = ((_priceAgreement.value as any).surchargeModifier ?? "").length > 0

    if (hasUnitPrice || hasSurchargeModifier) {
      if (_priceAgreement.value.isSaved) {
        await controller.update(_priceAgreement.value, requestConfig)
      } else {
        await controller.create(_priceAgreement.value, requestConfig)
      }
    } else {
      await controller.delete(_priceAgreement.value, requestConfig)
    }
  }

  function getUnitPriceKey(): keyof ProjectTransactionItem | keyof ProjectRentalItem {
    if (isTransactionItem(item)) {
      return "unitPrice"
    } else if (isRentalItem(item)) {
      return "unitPricePerDay"
    }
    throw new Error("Unknown project item type")
  }

  function getItemKey(): "purchaseItem" | "saleItem" | "rentalIntakeItem" | "rentalDispenseItem" {
    if (item instanceof PurchaseItem) {
      return "purchaseItem"
    } else if (item instanceof SaleItem) {
      return "saleItem"
    } else if (item instanceof RentalIntakeItem) {
      return "rentalIntakeItem"
    } else if (item instanceof RentalDispenseItem) {
      return "rentalDispenseItem"
    }
    throw new Error("Unknown item type")
  }

  function getLevelKey(): "project" | "knownBusinessEntity" {
    if (isProjectLevel()) {
      return "project"
    } else if (isKnownBusinessEntityLevel()) {
      return "knownBusinessEntity"
    }
    throw new Error("Unknown item level")
  }

  function getLevel(): Project | KnownBusinessEntity {
    if (isProjectLevel()) {
      return project
    } else if (isKnownBusinessEntityLevel()) {
      return project.executor.knownBusinessEntity!
    }
    throw new Error("Unknown item level")
  }

  function isProjectLevel() {
    return (
      itemType === ProjectPurchaseItem ||
      itemType === ProjectSaleItem ||
      itemType === ProjectRentalIntakeItem ||
      itemType === ProjectRentalDispenseItem
    )
  }

  function isKnownBusinessEntityLevel() {
    return (
      itemType === KnownBusinessEntitySaleItem || itemType === KnownBusinessEntityRentalDispenseItem
    )
  }

  return {
    priceAgreement,
    wasChanged,
    isRetrieving,
    savePriceAgreement
  }
}
